import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { AttackRelationshipGraph } from "@/components/under-attack/AttackRelationshipGraph";
import { fmtNumber, fmtPct, truncateMiddle } from "@/lib/format";
import type {
  UnderAttackCommonFindingV1,
  UnderAttackHotClusterV1,
  UnderAttackModeV1,
  UnderAttackRecommendationV1,
  UnderAttackSeedInputV1,
} from "@/types/v1";
import type { FilterState } from "@/types";

const EMPTY_SEEDS: UnderAttackSeedInputV1 = { ips: [], asns: [], ja4s: [], paths: [] };

const REGEX = {
  ip: /^[0-9a-fA-F:.%]{2,45}$/,
  asn: /^AS?\d{1,10}$/i,
  ja4: /^[a-z0-9]{1,16}_[A-Za-z0-9_-]{6,128}$/,
  path: /^[A-Za-z0-9._\-/?=&%:]{1,512}$/,
};

export function UnderAttackMode() {
  const { filters } = useFilters();
  const [raw, setRaw] = useState("");
  const parsed = useMemo(() => parseSeeds(raw), [raw]);
  const mutation = useMutation({
    mutationFn: (seeds: UnderAttackSeedInputV1) => api.underAttackMode(filters, seeds).then((r) => r.data),
  });

  const canRun = countSeeds(parsed.seeds) > 0 && parsed.invalid.length === 0 && !mutation.isPending;
  const data = mutation.data;

  return (
    <div className="flex flex-col gap-4">
      <Card className="border-sev-high/40 bg-sev-high/5">
        <CardHeader
          title={<span className="text-sev-high">Under Attack Mode</span>}
          subtitle="Incident-only workflow: paste one or many IPs, ASNs, JA4 fingerprints, and URL paths to identify hot related activity and draft a safe mitigation."
          action={<Badge tone="high">Incident only</Badge>}
        />
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
          <div className="space-y-3">
            <label className="block text-xs font-medium text-fg-muted" htmlFor="under-attack-seeds">Indicators to investigate</label>
            <textarea
              id="under-attack-seeds"
              value={raw}
              onChange={(event) => setRaw(event.target.value)}
              spellCheck={false}
              placeholder={"203.0.113.42\nAS13335\nt13d1516h2_8daaf6152771_b186095e22b6\n/Account/CreateToken"}
              className="min-h-40 w-full resize-y rounded-md border border-border-strong bg-surface-2 p-3 font-mono text-xs text-fg outline-none placeholder-fg-subtle focus:border-sev-high/60"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="default"
                className="border-sev-high/50 bg-sev-high/20 text-fg hover:bg-sev-high/30"
                disabled={!canRun}
                onClick={() => mutation.mutate(parsed.seeds)}
              >Run attack analysis</Button>
              <Button variant="ghost" disabled={!raw && !data} onClick={() => { setRaw(""); mutation.reset(); }}>Clear</Button>
              <span className="text-xs text-fg-subtle">Supports comma, space, or newline separated values.</span>
            </div>
            {parsed.invalid.length > 0 && (
              <div className="rounded-md border border-sev-high/40 bg-sev-high/10 px-3 py-2 text-xs text-sev-high">
                Could not classify: {parsed.invalid.slice(0, 8).join(", ")}{parsed.invalid.length > 8 ? "..." : ""}
              </div>
            )}
          </div>
          <SeedPreview seeds={parsed.seeds} invalidCount={parsed.invalid.length} />
        </div>
      </Card>

      <AnalysisScope filters={filters} />

      {mutation.isPending ? (
        <Skeleton className="h-96" />
      ) : mutation.error ? (
        <Card><ErrorState error={mutation.error} onRetry={() => mutation.mutate(parsed.seeds)} /></Card>
      ) : data ? (
        <Results data={data} />
      ) : (
        <Card>
          <EmptyState title="No attack analysis has been run yet." hint="Paste incident indicators above to look for related hot paths, JA4s, ASNs, and IPs." />
        </Card>
      )}
    </div>
  );
}

function AnalysisScope({ filters }: { filters: FilterState }) {
  const applied = scopeChips(filters);
  const ignored = ignoredRiskChips(filters);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-1 px-3 py-2 text-xs">
      <span className="font-medium text-fg">Analysis scope</span>
      {applied.length ? applied.map((chip) => (
        <span key={chip} className="rounded-md border border-border-strong bg-surface-2 px-2 py-0.5 text-fg-muted">{chip}</span>
      )) : <span className="text-fg-subtle">Only the selected time window is constraining this run.</span>}
      {ignored.length > 0 && (
        <span className="ml-auto text-[11px] text-sev-suspicious" title="Under Attack Mode expands entered indicators regardless of risk score so low-scoring related traffic can still be assessed for collateral.">
          Ignoring risk filters: {ignored.join(", ")}
        </span>
      )}
    </div>
  );
}

function SeedPreview({ seeds, invalidCount }: { seeds: UnderAttackSeedInputV1; invalidCount: number }) {
  return (
    <div className="rounded-lg border border-border bg-surface-1 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-fg">Detected values</div>
        <Badge tone={invalidCount ? "high" : countSeeds(seeds) ? "good" : "neutral"}>{fmtNumber(countSeeds(seeds))} ready</Badge>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <SeedGroup label="IPs" values={seeds.ips} />
        <SeedGroup label="ASNs" values={seeds.asns.map((asn) => `AS${asn}`)} />
        <SeedGroup label="JA4" values={seeds.ja4s} mono />
        <SeedGroup label="Paths" values={seeds.paths} mono />
      </div>
    </div>
  );
}

function SeedGroup({ label, values, mono }: { label: string; values: string[]; mono?: boolean }) {
  return (
    <div className="min-h-16 rounded-md border border-border bg-surface-2 p-2">
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-fg-subtle">{label}</div>
      {values.length ? (
        <div className="flex flex-wrap gap-1.5">
          {values.slice(0, 8).map((value) => (
            <span key={value} className={clsx("rounded border border-border-strong bg-surface-1 px-1.5 py-0.5 text-[11px] text-fg", mono && "font-mono")} title={value}>
              {truncateMiddle(value, 28)}
            </span>
          ))}
          {values.length > 8 && <span className="text-[11px] text-fg-subtle">+{values.length - 8} more</span>}
        </div>
      ) : <div className="text-xs text-fg-subtle">None</div>}
    </div>
  );
}

function Results({ data }: { data: UnderAttackModeV1 }) {
  const best = data.recommendations[0];
  return (
    <>
      <SummaryStrip data={data} />
      <CommonFindingsStrip findings={data.commonFindings} />
      {best && <RecommendationHero recommendation={best} />}
      <AttackRelationshipGraph clusters={data.clusters} seeds={data.seeds} />
      <ClustersTable clusters={data.clusters} />
      <RecommendationsTable recommendations={data.recommendations} />
      {best && <CollateralImpactSection recommendation={best} />}
    </>
  );
}

function SummaryStrip({ data }: { data: UnderAttackModeV1 }) {
  const items = [
    ["Matched events", fmtNumber(data.summary.matchedEvents, { compact: true })],
    ["High-risk events", fmtNumber(data.summary.highRisk, { compact: true })],
    ["High-risk share", fmtPct(data.summary.highRiskShare * 100)],
    ["Hot clusters", fmtNumber(data.summary.clusterCount)],
    ["Recommendations", fmtNumber(data.summary.recommendationCount)],
    ["Strongest signal", data.summary.strongestSignal?.toUpperCase() ?? "-"],
  ];
  return (
    <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
      {items.map(([label, value]) => (
        <Card key={label}>
          <div className="text-[11px] uppercase tracking-wide text-fg-subtle">{label}</div>
          <div className="mt-1 text-xl font-semibold tabular text-fg">{value}</div>
        </Card>
      ))}
    </div>
  );
}

function CommonFindingsStrip({ findings }: { findings: UnderAttackCommonFindingV1[] }) {
  if (!findings.length) return null;
  return (
    <Card className="border-sev-suspicious/30">
      <CardHeader title="Common findings" subtitle="Shared pivots observed across the entered seeds. Recommendations prefer these when coverage and collateral are acceptable." />
      <div className="grid gap-2 md:grid-cols-3">
        {findings.slice(0, 6).map((finding) => (
          <div key={`${finding.dimension}:${finding.value}`} className="rounded-md border border-border bg-surface-2 p-3">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-subtle">{finding.dimension}</span>
              <Badge tone={finding.matchedSeeds === finding.enteredSeeds ? "good" : "suspicious"}>{finding.matchedSeeds}/{finding.enteredSeeds} seeds</Badge>
            </div>
            <div className="truncate font-mono text-xs text-fg" title={finding.value}>{truncateMiddle(finding.value, 42)}</div>
            <div className="mt-2 text-[11px] text-fg-subtle">{fmtNumber(finding.highRisk, { compact: true })} high-risk / {fmtNumber(finding.events, { compact: true })} events</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RecommendationHero({ recommendation }: { recommendation: UnderAttackRecommendationV1 }) {
  return (
    <Card className="border-sev-high/35">
      <CardHeader
        title="Recommended mitigation"
        subtitle={recommendation.rationale}
        action={<Badge tone={recommendation.action === "block" ? "high" : "suspicious"}>{recommendation.action}</Badge>}
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-fg-subtle">Predicate</div>
            <div className="text-sm text-fg">{recommendation.predicate}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {recommendation.predicateDimensions.map((dimension) => (
                <span key={dimension} className="rounded border border-border-strong bg-surface-2 px-1.5 py-0.5 text-[11px] uppercase tracking-wide text-fg-muted">
                  {dimension} in rule scope
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-fg-subtle">Cloudflare expression draft</div>
            <pre className="overflow-x-auto rounded-md border border-border bg-surface-2 p-3 font-mono text-xs text-fg">{recommendation.ruleExpression}</pre>
          </div>
          {recommendation.warnings.length > 0 && (
            <div className="rounded-md border border-sev-suspicious/40 bg-sev-suspicious/10 px-3 py-2 text-xs text-sev-suspicious">
              {recommendation.warnings[0]}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Metric label="Recommended duration for the rule to be active" value={formatDuration(recommendation.recommendedDurationMinutes)} title="How long this temporary mitigation should stay active before expiring or being reviewed." />
          <Metric label="Confidence" value={recommendation.confidence} />
          <Metric label="Seed coverage" value={formatSeedCoverage(recommendation.seedCoverage)} title="How many entered seed values are covered by the proposed rule candidate." />
          <Metric label="Rate limit" value={`${fmtNumber(recommendation.rateLimit.requests)} / ${recommendation.rateLimit.periodSeconds}s`} />
          <Metric label="Peak observed" value={fmtNumber(recommendation.rateLimit.observedPeak)} />
          <Metric label="Rule high-risk matched" value={fmtNumber(recommendation.ruleImpact.highRisk, { compact: true })} title="High-risk events that the proposed rule would match in the selected window." />
          <Metric label="Seed evidence" value={fmtNumber(recommendation.seedEvidence.highRisk, { compact: true })} title="High-risk events in the seed-expanded hot cluster that led to this candidate." />
          <Metric label="Possible collateral" value={fmtPct(recommendation.ruleImpact.collateralShare * 100)} title="Traffic matched by the proposed rule that is not high-risk or suspicious in the selected window." />
        </div>
      </div>
    </Card>
  );
}

function Metric({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-2 p-2" title={title}>
      <div className="text-[11px] uppercase tracking-wide text-fg-subtle">{label}</div>
      <div className="mt-1 font-semibold tabular text-fg">{value}</div>
    </div>
  );
}

function ClustersTable({ clusters }: { clusters: UnderAttackHotClusterV1[] }) {
  return (
    <Card>
      <CardHeader title="Hot related activity" subtitle="Top correlated clusters found from the entered indicators." />
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="text-fg-subtle">
            <tr className="border-b border-border">
              <th className="py-2 pr-3 font-medium">Path</th>
              <th className="py-2 pr-3 font-medium">JA4</th>
              <th className="py-2 pr-3 font-medium">ASN</th>
              <th className="py-2 pr-3 text-right font-medium">Events</th>
              <th className="py-2 pr-3 text-right font-medium" title="Events with risk score 80 or higher.">High-risk events</th>
              <th className="py-2 pr-3 text-right font-medium" title="95% of matching traffic scored at or below this value.">Upper risk score</th>
              <th className="py-2 text-right font-medium">Seed coverage</th>
            </tr>
          </thead>
          <tbody>
            {clusters.map((cluster) => (
              <tr key={cluster.id} className="border-b border-border/60 last:border-0">
                <td className="max-w-72 py-2 pr-3 font-mono text-fg" title={cluster.path ?? undefined}>{truncateMiddle(cluster.path ?? "-", 44)}</td>
                <td className="max-w-56 py-2 pr-3 font-mono text-fg-muted" title={cluster.ja4 ?? undefined}>{truncateMiddle(cluster.ja4 ?? "-", 28)}</td>
                <td className="py-2 pr-3 tabular text-fg-muted">{cluster.asn ? `AS${cluster.asn}` : "-"}</td>
                <td className="py-2 pr-3 text-right tabular text-fg">{fmtNumber(cluster.events, { compact: true })}</td>
                <td className="py-2 pr-3 text-right tabular text-sev-high">{fmtNumber(cluster.highRisk, { compact: true })}</td>
                <td className="py-2 pr-3 text-right tabular text-fg">{fmtNumber(cluster.p95Score)}</td>
                <td className="py-2 text-right tabular text-fg-muted">{formatSeedCoverage(cluster.seedCoverage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RecommendationsTable({ recommendations }: { recommendations: UnderAttackRecommendationV1[] }) {
  return (
    <Card>
      <CardHeader title="Recommendation candidates" subtitle="Compare precision, coverage, and collateral before acting." />
      {recommendations.length === 0 ? <EmptyState title="No safe recommendation candidates found." /> : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="text-fg-subtle">
              <tr className="border-b border-border">
                <th className="py-2 pr-3 font-medium">Candidate</th>
                <th className="py-2 pr-3 font-medium">Action</th>
                <th className="py-2 pr-3 text-right font-medium" title="Events with risk score 80 or higher.">High-risk events</th>
                <th className="py-2 pr-3 text-right font-medium" title="Traffic that would also match this rule but may not be part of the attack.">Possible collateral</th>
                <th className="py-2 pr-3 text-right font-medium">Seed coverage</th>
                <th className="py-2 pr-3 text-right font-medium">Rate threshold</th>
                <th className="py-2 font-medium">Investigate</th>
              </tr>
            </thead>
            <tbody>
              {recommendations.map((rec) => (
                <tr key={rec.id} className="border-b border-border/60 last:border-0">
                  <td className="max-w-[520px] py-2 pr-3 text-fg">
                    <div className="font-medium">{rec.title}</div>
                    <div className="mt-0.5 truncate font-mono text-[11px] text-fg-subtle" title={rec.predicate}>{rec.predicate}</div>
                  </td>
                  <td className="py-2 pr-3"><Badge tone={rec.action === "block" ? "high" : "suspicious"}>{rec.action}</Badge></td>
                  <td className="py-2 pr-3 text-right tabular text-sev-high">{fmtNumber(rec.ruleImpact.highRisk, { compact: true })}</td>
                  <td className="py-2 pr-3 text-right tabular text-fg">{fmtPct(rec.ruleImpact.collateralShare * 100)}</td>
                  <td className="py-2 pr-3 text-right tabular text-fg-muted">{formatSeedCoverage(rec.seedCoverage)}</td>
                  <td className="py-2 pr-3 text-right tabular text-fg-muted">{fmtNumber(rec.rateLimit.requests)} / {rec.rateLimit.periodSeconds}s</td>
                  <td className="py-2"><Button size="sm" variant="ghost" onClick={() => pivotToInvestigations(rec.filterPreset)}>Open</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function CollateralImpactSection({ recommendation }: { recommendation: UnderAttackRecommendationV1 }) {
  const collateral = recommendation.collateral;
  const fixedIp = recommendation.predicateDimensions.includes("ip");
  const fixedAsn = recommendation.predicateDimensions.includes("asn");
  return (
    <Card>
      <CardHeader
        title="Possible collateral impact if implemented"
        subtitle="Traffic in the selected time window that matches the recommended rule. Fixed rule dimensions are shown as scope; broad dimensions show the top matched values."
      />
      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(420px,1.1fr)]">
        <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-3 xl:grid-cols-2">
          <Metric label="Matched by rule" value={fmtNumber(recommendation.ruleImpact.total, { compact: true })} />
          <Metric label="High-risk matched" value={fmtNumber(recommendation.ruleImpact.highRisk, { compact: true })} title="Events with risk score 80 or higher that the proposed rule would match." />
          <Metric label="Likely human" value={fmtNumber(collateral.likelyHuman, { compact: true })} />
          <Metric label="Verified bots" value={fmtNumber(collateral.verifiedBots, { compact: true })} />
          <Metric label="Successful requests" value={fmtNumber(collateral.successful, { compact: true })} />
          <Metric label="Affected businesses" value={fmtNumber(collateral.distinctBusinesses, { compact: true })} />
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <CollateralList title="Paths matched by rule" values={collateral.topPaths} />
          <CollateralList title={fixedIp ? "IP fixed by rule" : "Source IPs matched by rule"} values={collateral.topIps} />
          <CollateralList title={fixedAsn ? "ASN fixed by rule" : "ASNs matched by rule"} values={collateral.topAsns.map((asn) => `AS${asn}`)} />
        </div>
      </div>
    </Card>
  );
}

function CollateralList({ title, values }: { title: string; values: string[] }) {
  return (
    <div className="rounded-md border border-border bg-surface-2 p-3">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-fg-subtle">{title}</div>
      {values.length ? (
        <div className="space-y-1">
          {values.slice(0, 6).map((value) => (
            <div key={value} className="truncate font-mono text-xs text-fg" title={value}>{value}</div>
          ))}
        </div>
      ) : <div className="text-xs text-fg-subtle">None observed</div>}
    </div>
  );
}

function scopeChips(filters: FilterState): string[] {
  const chips: string[] = [`Time: ${filters.timeRange}`];
  if (filters.endpointGroups?.length) chips.push(`Area: ${filters.endpointGroups.join(", ")}`);
  if (filters.endpointType) chips.push(`Endpoint: ${filters.endpointType}`);
  if (filters.pathPrefix) chips.push(`Path prefix: ${filters.pathPrefix}`);
  if (filters.method) chips.push(`Method: ${filters.method}`);
  if (filters.country) chips.push(`Country: ${filters.country}`);
  if (filters.colo) chips.push(`Colo: ${filters.colo}`);
  if (filters.asn) chips.push(`ASN: AS${filters.asn}`);
  if (filters.ip) chips.push(`IP: ${filters.ip}`);
  if (filters.ja4) chips.push(`JA4: ${truncateMiddle(filters.ja4, 24)}`);
  if (filters.botScoreBucket) chips.push(`Bot score: ${filters.botScoreBucket}`);
  if (filters.threatScoreMin != null) chips.push(`Threat score >= ${filters.threatScoreMin}`);
  if (filters.threatScoreMax != null) chips.push(`Threat score <= ${filters.threatScoreMax}`);
  if (filters.verifiedBot != null) chips.push(`Verified bot: ${filters.verifiedBot ? "true" : "false"}`);
  if (filters.managedList) chips.push(`Managed list: ${filters.managedList}`);
  if (filters.action) chips.push(`Action: ${filters.action}`);
  if (filters.outcome) chips.push(`Outcome: ${filters.outcome}`);
  return chips;
}

function ignoredRiskChips(filters: FilterState): string[] {
  const ignored: string[] = [];
  if (filters.riskBands?.length) ignored.push(`Risk band ${filters.riskBands.join(", ")}`);
  if (filters.scoreMin != null) ignored.push(`Score min ${filters.scoreMin}`);
  if (filters.scoreMax != null) ignored.push(`Score max ${filters.scoreMax}`);
  return ignored;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours} hr` : `${hours.toFixed(1)} hr`;
}

function formatSeedCoverage(coverage: { entered: number; matched: number; dimension: string | null }): string {
  if (!coverage.entered || !coverage.dimension) return "-";
  return `${coverage.matched}/${coverage.entered} ${coverage.dimension}`;
}

function parseSeeds(raw: string): { seeds: UnderAttackSeedInputV1; invalid: string[] } {
  const seeds: UnderAttackSeedInputV1 = { ...EMPTY_SEEDS, ips: [], asns: [], ja4s: [], paths: [] };
  const invalid: string[] = [];
  const tokens = raw.split(/[\s,;]+/).map((value) => value.trim()).filter(Boolean);
  for (const token of tokens) {
    const normalizedUrlPath = urlPath(token);
    if (normalizedUrlPath && REGEX.path.test(normalizedUrlPath)) seeds.paths.push(normalizedUrlPath);
    else if (REGEX.ja4.test(token)) seeds.ja4s.push(token);
    else if (REGEX.asn.test(token)) seeds.asns.push(token.replace(/^AS/i, ""));
    else if (token.startsWith("/") && REGEX.path.test(token)) seeds.paths.push(token);
    else if (REGEX.ip.test(token) && /[.:]/.test(token)) seeds.ips.push(token);
    else invalid.push(token);
  }
  return { seeds: dedupeSeeds(seeds), invalid };
}

function urlPath(value: string): string | null {
  if (!/^https?:\/\//i.test(value)) return null;
  try {
    const url = new URL(value);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

function dedupeSeeds(seeds: UnderAttackSeedInputV1): UnderAttackSeedInputV1 {
  return {
    ips: [...new Set(seeds.ips)].slice(0, 50),
    asns: [...new Set(seeds.asns)].slice(0, 50),
    ja4s: [...new Set(seeds.ja4s)].slice(0, 50),
    paths: [...new Set(seeds.paths)].slice(0, 50),
  };
}

function countSeeds(seeds: UnderAttackSeedInputV1): number {
  return seeds.ips.length + seeds.asns.length + seeds.ja4s.length + seeds.paths.length;
}

function pivotToInvestigations(filters: Record<string, string>) {
  const url = new URL(window.location.href);
  url.searchParams.set("tab", "investigations");
  for (const [key, value] of Object.entries(filters)) url.searchParams.set(key, value);
  window.history.pushState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
